import { App, Button, Card, List, Modal, Popconfirm, Select, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useAuth } from '../../../auth/AuthProvider'
import { isSysAdmin } from '../../../utils/roles'
import { useZealysLogins } from '../../internal-access/hooks/useZealysLogins'
import { useTenantPermissionUsers } from '../hooks/useTenantPermissionUsers'
import { grantPermission, revokePermission, type PermissionUser } from '../services/tenant-permission-api'

const { Text } = Typography

interface InternalPermission {
  id: string
  label: string
  action: string
}

const INTERNAL_PERMISSIONS: InternalPermission[] = [
  { id: 'eb615de8-178d-4edc-ac61-145aaa67b6b0', label: 'Feature flag management', action: 'vm' },
  { id: '6a89ebad-2a68-42c0-81c7-f2be9d23d44f', label: 'Branding & colors', action: 'vm' }
]

interface TenantPermissionManagersProps {
  tenantCode: string
}

export function TenantPermissionManagers({ tenantCode }: TenantPermissionManagersProps) {
  const { user } = useAuth()
  const canManage = isSysAdmin(user) && user?.profile?.email === 'tosco.ong@zealys.com'

  return (
    <Card title="Internal permissions" size="small" style={{ marginBottom: 16 }}>
      {INTERNAL_PERMISSIONS.map(permission => (
        <PermissionSection key={permission.id} tenantCode={tenantCode} permission={permission} canManage={canManage} />
      ))}
    </Card>
  )
}

interface PermissionSectionProps {
  tenantCode: string
  permission: InternalPermission
  canManage: boolean
}

function PermissionSection({ tenantCode, permission, canManage }: PermissionSectionProps) {
  const { message } = App.useApp()
  const { users, loading, refresh } = useTenantPermissionUsers(tenantCode, permission.id)
  const { logins } = useZealysLogins()
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedLoginId, setSelectedLoginId] = useState<string | null>(null)
  const [granting, setGranting] = useState(false)

  const availableLogins = logins.filter(login => !users.some(u => u.loginId === login.id))

  const handleGrant = async () => {
    if (!selectedLoginId) return

    try {
      setGranting(true)
      await grantPermission({
        loginId: selectedLoginId,
        sysPermissionId: permission.id,
        tenantCode,
        action: permission.action
      })
      message.success('Permission granted')
      setAddModalOpen(false)
      setSelectedLoginId(null)
      refresh()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to grant permission')
    } finally {
      setGranting(false)
    }
  }

  const handleRevoke = async (id: string) => {
    try {
      await revokePermission(id)
      message.success('Permission revoked')
      refresh()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to revoke permission')
    }
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <Text strong style={{ fontSize: 13 }}>
        {permission.label}
      </Text>
      {canManage && (
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={e => {
            e.stopPropagation()
            setAddModalOpen(true)
          }}
        >
          Add
        </Button>
      )}
    </div>
  )

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        {header}
        {loading ? (
          <Text type="secondary" style={{ fontSize: 12, paddingLeft: 4 }}>
            Loading...
          </Text>
        ) : users.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12, paddingLeft: 4 }}>
            No users assigned
          </Text>
        ) : (
          <UserList users={users} canManage={canManage} onRevoke={handleRevoke} />
        )}
      </div>

      <Modal
        title={`Add ${permission.label.toLowerCase()} access`}
        open={addModalOpen}
        onOk={handleGrant}
        onCancel={() => {
          setAddModalOpen(false)
          setSelectedLoginId(null)
        }}
        confirmLoading={granting}
        okText="Grant Access"
        okButtonProps={{ disabled: !selectedLoginId }}
      >
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Select a user to grant {permission.label.toLowerCase()} access for this workspace.
          </Text>
          <Select
            showSearch
            placeholder="Search by name or email"
            value={selectedLoginId}
            onChange={setSelectedLoginId}
            optionFilterProp="label"
            style={{ width: '100%' }}
            options={availableLogins.map(login => ({
              value: login.id,
              label: `${login.loginName || login.loginCode} (${login.email})`
            }))}
          />
        </div>
      </Modal>
    </>
  )
}

interface UserListProps {
  users: PermissionUser[]
  canManage: boolean
  onRevoke: (id: string) => void
}

function UserList({ users, canManage, onRevoke }: UserListProps) {
  return (
    <List
      size="small"
      dataSource={users}
      renderItem={item => (
        <List.Item
          style={{ padding: '4px 0 4px 4px' }}
          actions={
            canManage
              ? [
                  <Popconfirm
                    key="revoke"
                    title="Revoke access?"
                    description={`Remove access for ${item.loginName || item.email}?`}
                    onConfirm={() => onRevoke(item.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                ]
              : undefined
          }
        >
          <Text style={{ fontSize: 13 }}>{item.loginName || item.email}</Text>
        </List.Item>
      )}
    />
  )
}
